import React, { useContext, useEffect, useState } from "react";
import { Button, Card, Checkbox, Form, Spin } from "antd";
// import PaymentGateway from "../../../helpers/PaymentGateway";
import "./HotelCheckout.scss";
import { LoadingOutlined } from "@ant-design/icons";
import PayGateway from "../../../helpers/PayGateway";
import GlobalStatesContext from "../../../common/providers/GlobalStatesContext";
import PayMentSelection from "../../../common/PaymentSelection/PaymentSelection";
import { useAuthContext } from "../../../common/providers/AuthProvider";
import ApiClient from "../../../helpers/ApiClient";

/** Admin ServiceType / IsHold / IsDeposit flags: 1 Flights, 2 Hotels, … */
const HOTEL_SERVICE_TYPE = 2;

const parseServiceFlags = (raw) => {
  if (Array.isArray(raw)) {
    return raw
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0);
  }
  if (raw == null || raw === "") {
    return [];
  }
  return String(raw)
    .split(/[$|,]/)
    .map((s) => Number(String(s).trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
};

const hasHotelServiceFlag = (raw) =>
  parseServiceFlags(raw).includes(HOTEL_SERVICE_TYPE);

const extractHoldDepositFromAuthResponse = (resp) => {
  const root = resp?.data ?? resp ?? {};
  const nested = root?.data && typeof root.data === "object" ? root.data : null;
  return {
    Hold: root?.Hold ?? nested?.Hold,
    Deposit: root?.Deposit ?? nested?.Deposit,
    Services: root?.Services ?? nested?.Services,
  };
};

const HOTEL_REVIEW_PG_STORAGE_KEY = "syt_hotel_review_pg_selection";

const saveHotelReviewPgSelection = (traceId, hotelId, payload) => {
  if (!traceId) {
    return;
  }
  try {
    sessionStorage.setItem(
      HOTEL_REVIEW_PG_STORAGE_KEY,
      JSON.stringify({
        traceId: String(traceId),
        hotelId: String(hotelId ?? ""),
        ...payload,
      }),
    );
  } catch {
    /* ignore */
  }
};

const loadHotelReviewPgSelection = (traceId, hotelId) => {
  if (!traceId) {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(HOTEL_REVIEW_PG_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (parsed?.traceId !== String(traceId)) {
      return null;
    }
    const savedHotelId = String(parsed?.hotelId ?? "").trim();
    const currentHotelId = String(hotelId ?? "").trim();
    if (savedHotelId && currentHotelId && savedHotelId !== currentHotelId) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const HotelBookPayCard = (props) => {
  const {
    user,
    isLogin: { agent },
    setUser,
  } = useAuthContext();
  const [form] = Form.useForm();
  const {
    state: { SeletedPayOption, otherData: { transactionFee } },
    OnPaymentModeSelection,
    AddTransFee,
  } = useContext(GlobalStatesContext);
  const [value, setValue] = useState(() => {
    const method = String(transactionFee?.methodType ?? "").trim();
    return method || undefined;
  });
  const [hotelHoldAllowed, setHotelHoldAllowed] = useState(() =>
    hasHotelServiceFlag(user?.Hold),
  );
  const [holdPermissionReady, setHoldPermissionReady] = useState(() =>
    hasHotelServiceFlag(user?.Hold),
  );
  const [Deposit, setDeposit] = useState(() =>
    hasHotelServiceFlag(user?.Deposit),
  );

  // Keep in sync when session user.Hold changes (login / auth refresh).
  useEffect(() => {
    setHotelHoldAllowed(hasHotelServiceFlag(user?.Hold));
    setDeposit(hasHotelServiceFlag(user?.Deposit));
    if (hasHotelServiceFlag(user?.Hold)) {
      setHoldPermissionReady(true);
    }
  }, [user?.Hold, user?.Deposit]);

  useEffect(() => {
    let cancelled = false;
    ApiClient.get("admin/getUserByauthToken")
      .then((resp) => {
        if (cancelled) {
          return;
        }
        const parsed = extractHoldDepositFromAuthResponse(resp);
        // Prefer API Hold when present; otherwise keep session flags.
        const holdRaw =
          parsed.Hold !== undefined && parsed.Hold !== null
            ? parsed.Hold
            : user?.Hold;
        const depositRaw =
          parsed.Deposit !== undefined && parsed.Deposit !== null
            ? parsed.Deposit
            : user?.Deposit;
        const allowHold = hasHotelServiceFlag(holdRaw);
        setHotelHoldAllowed(allowHold);
        setDeposit(hasHotelServiceFlag(depositRaw));
        if (
          (parsed.Hold !== undefined || parsed.Deposit !== undefined) &&
          typeof setUser === "function"
        ) {
          setUser((prev) =>
            prev
              ? {
                  ...prev,
                  Hold: parsed.Hold ?? prev.Hold,
                  Deposit: parsed.Deposit ?? prev.Deposit,
                  Services: parsed.Services ?? prev.Services,
                }
              : prev,
          );
        }
        setHoldPermissionReady(true);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        // Network fail → trust current session (after logout/login Admin flags apply).
        setHotelHoldAllowed(hasHotelServiceFlag(user?.Hold));
        setDeposit(hasHotelServiceFlag(user?.Deposit));
        setHoldPermissionReady(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only permission refresh
  }, []);

  const traceId =
    props.hotelTraceId ??
    props.traceId ??
    "";
  const hotelId =
    props.hotelId ??
    props.hotelCode ??
    "";

  const isReviewPage = props.bookpaycardinfo === "hotel-review";

  useEffect(() => {
    if (isReviewPage && !props.showPayBookOptions && !props.showReviewPanFields) {
      return;
    }
    const saved = loadHotelReviewPgSelection(traceId, hotelId);
    const method = String(
      transactionFee?.methodType ?? saved?.paymentMethod ?? "",
    ).trim();
    if (method) {
      setValue((prev) => (prev === method ? prev : method));
      if (!transactionFee?.methodType && typeof AddTransFee === "function") {
        AddTransFee(method);
      }
    }
    if (Number(saved?.pgType) === 1 && !SeletedPayOption) {
      OnPaymentModeSelection({ target: { value: 1 } });
    }
  }, [traceId, hotelId, transactionFee?.methodType, AddTransFee, OnPaymentModeSelection, SeletedPayOption, isReviewPage, props.showPayBookOptions, props.showReviewPanFields]);

  const handlePaymentMethodChange = (method) => {
    setValue(method);
    saveHotelReviewPgSelection(traceId, hotelId, {
      pgType: 1,
      paymentMethod: method,
    });
  };

  const handleContinueToPg = () => {
    const method = String(value || transactionFee?.methodType || "").trim();
    if (method) {
      saveHotelReviewPgSelection(traceId, hotelId, { pgType: 1, paymentMethod: method });
    }
    props.blockApiReq(1);
  };

  const selectedPaymentMethod =
    value || transactionFee?.methodType || undefined;
  const savedPgSelection = loadHotelReviewPgSelection(traceId, hotelId);
  const restoredPgType = savedPgSelection?.pgType ?? null;

  const goTo = () => {
    if (props.bookpaycardinfo === "hotel-review") {
      props.processPayGateway(1);
    } else if (props.bookpaycardinfo === "hotel-checkout") {
      props.redirectToPreview();
    }
  };

  const antIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "#fff" }} spin />
  );

  const normalizedPurchaseType = String(props.purchaseType ?? "")
    .trim()
    .toLowerCase();
  // Hide Hold ONLY when Tripjack explicitly Instant-only (isBA=false).
  // isBA unknown + purchaseType Instant must NOT hide Hold — v3 often omits isBA
  // while Tripjack portal still shows Hold/Block (free-cancel rates).
  const isInstantOnly = props.tripjackIsBA === false;
  const canHoldByPurchaseType =
    !isInstantOnly &&
    (normalizedPurchaseType === "block" ||
      normalizedPurchaseType === "hold" ||
      normalizedPurchaseType === "2" ||
      props.tripjackIsBA === true);
  const isAgentUser = Boolean(
    props.agent || agent || user?.Role?.RoleId === 5,
  );
  const isNonRefundableSelection = props.isNonRefundableSelection === true;
  const shouldShowHold = Boolean(
    holdPermissionReady &&
      props.bookpaycardinfo === "hotel-review" &&
      isAgentUser &&
      hotelHoldAllowed &&
      !isInstantOnly &&
      !isNonRefundableSelection &&
      (props.tripjackIsBA === true ||
        canHoldByPurchaseType ||
        props.hasZeroChargeCancellation === true ||
        props.hasPayableAmount === false ||
        (normalizedPurchaseType === "" && props.hasPayableAmount === false)),
  );
  const paymentGatewayVisible =
    isReviewPage &&
    Boolean(props.showReviewPanFields || props.showPayBookOptions);
  // Hold visible = can Hold; Instant-only when isBA=false.
  const primaryBookLabel =
    props.bookpaycardinfo === "hotel-checkout"
      ? "Continue"
      : isInstantOnly && isAgentUser
        ? "Instant Book"
        : "Pay and Book";

  return (
    <React.Fragment>
      <Card bordered={false} className="hotel-card-wrapper book-pay-card">
        <Form form={form} onFinish={goTo}>
          {props.bookpaycardinfo === "hotel-checkout" ? (
            <div className="book-pay-tc">
              <Form.Item
                name="remember"
                rules={[
                  {
                    validator: (rule, value) => {
                      return value
                        ? Promise.resolve()
                        : Promise.reject("Accept Terms & Conditions");
                    },
                  },
                ]}
                valuePropName="checked"
              >
                <Checkbox>
                  I Agree To All The{" "}
                  <a
                    href="/termsofconditions"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {" Terms & Conditions"}{" "}
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacypolicy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {" "}
                    Privacy Policy.
                  </a>
                </Checkbox>
              </Form.Item>
            </div>
          ) : null}
          <div className="d-flex flex-nowrap">
            <div className="book-pay-btn mr-2 mb-1">
              <Button
                disabled={props.isLoading}
                className="btn btn-md"
                htmlType={isReviewPage ? "button" : "submit"}
                onClick={
                  isReviewPage ? () => props.processPayGateway(1) : undefined
                }
              >
                {primaryBookLabel}
              </Button>
            </div>

            {shouldShowHold ? (
              <div className="book-pay-btn mb-1">
                {props.holdLoading ? (
                  <Button className="btn btn-md" disabled>
                    <Spin indicator={antIcon} /> Holding...
                  </Button>
                ) : (
                  <Button
                    className="btn btn-md"
                    disabled={props.isLoading || props.holdLoading}
                    onClick={() => props.processPayGateway(2)}
                  >
                    Hold Hotel
                  </Button>
                )}
              </div>
            ) : null}
          </div>
        </Form>
        {paymentGatewayVisible && (
          <PayGateway
            blockApiReq={props.blockApiReq}
            loadingSpin={props.loadingSpin}
            OnPaymentModeSelection={(event) => {
              OnPaymentModeSelection(event);
              const pgType = Number(event?.target?.value);
              if (pgType === 1) {
                saveHotelReviewPgSelection(traceId, hotelId, {
                  pgType: 1,
                  paymentMethod: selectedPaymentMethod || value,
                });
              } else if (pgType === 3) {
                saveHotelReviewPgSelection(traceId, hotelId, {
                  pgType: 3,
                  paymentMethod: "",
                });
              }
            }}
            IsDeposit={Deposit}
            hideContinueButton={isReviewPage && SeletedPayOption}
            initialPgType={restoredPgType}
          />
        )}
      </Card>
      {isReviewPage && paymentGatewayVisible && SeletedPayOption ? (
        <>
          <PayMentSelection
            setValue={handlePaymentMethodChange}
            value={selectedPaymentMethod}
          />
          <div className="book-pay-btn mt-2">
            {props.loadingSpin ? (
              <Button className="payment-btn payment-btn--active" disabled>
                <Spin indicator={antIcon} /> Processing...
              </Button>
            ) : (
              <Button
                className="payment-btn"
                disabled={!selectedPaymentMethod}
                onClick={handleContinueToPg}
              >
                Continue
              </Button>
            )}
          </div>
        </>
      ) : null}
    </React.Fragment>
  );
};

export default HotelBookPayCard;
